import React, { useEffect, useState, useRef, useCallback } from 'react';
import { chatApi, clientsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  MessageSquare, Send, Search, Archive, ArchiveRestore,
  Clock, Umbrella, User, Settings, Paperclip, MoreVertical,
  Phone, Smartphone, Monitor, Mail, Plus, X,
} from 'lucide-react';
import { useTour } from '../hooks/useTour';
import { messagingTourSteps } from '../tours/messagingTour';
import './Nachrichten.css';

/* ============================================================
   Types
   ============================================================ */

interface Conversation {
  id: string;
  client_id: string;
  preferred_channel: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  is_archived: boolean;
  archived_at: string | null;
  archive_reason: string | null;
  created_at: string;
  client_name: string | null;
}

interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_type: string;
  channel: string;
  content: string;
  content_type: string;
  file_url: string | null;
  status: string;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
}

interface AutoReplyRule {
  id: string;
  rule_type: string;
  is_active: boolean;
  message_de: string;
  message_en: string | null;
  schedule: any;
  updated_at: string;
}

interface ArchiveSettings {
  retention_months: number;
  auto_archive_on_case_close: boolean;
  auto_archive_inactive_days: number;
  auto_upload_drive: boolean;
}

/* ============================================================
   Helpers
   ============================================================ */

const channelIcon = (ch: string) => {
  switch (ch) {
    case 'whatsapp': return <Smartphone size={14} />;
    case 'teams': return <Monitor size={14} />;
    case 'email': return <Mail size={14} />;
    default: return <MessageSquare size={14} />;
  }
};

const channelLabel = (ch: string) => {
  switch (ch) {
    case 'whatsapp': return 'WhatsApp';
    case 'teams': return 'Teams';
    case 'email': return 'E-Mail';
    default: return 'In-App';
  }
};

const ruleTypeLabel = (rt: string) => {
  switch (rt) {
    case 'busy': return 'Im Coaching';
    case 'outside_hours': return 'Geschäftszeiten';
    case 'vacation': return 'Urlaub';
    default: return rt;
  }
};

const ruleTypeIcon = (rt: string) => {
  switch (rt) {
    case 'busy': return <User size={16} />;
    case 'outside_hours': return <Clock size={16} />;
    case 'vacation': return <Umbrella size={16} />;
    default: return <Clock size={16} />;
  }
};

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 604800000) {
    return d.toLocaleDateString('de-DE', { weekday: 'short' });
  }
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

const getInitials = (name: string | null) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
};

const avatarColors = ['#6366F1', '#0D9488', '#EA580C', '#3B82F6', '#8B5CF6', '#EC4899'];
const getAvatarColor = (name: string | null) => {
  if (!name) return avatarColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

/* ============================================================
   Component
   ============================================================ */

export default function Nachrichten() {
  const { showToast } = useToast();
  useTour('messaging', messagingTourSteps);

  // State
  const [tab, setTab] = useState<'aktiv' | 'archiv'>('aktiv');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [archivedConvs, setArchivedConvs] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Auto-Reply State
  const [autoReplies, setAutoReplies] = useState<AutoReplyRule[]>([]);
  const [showAutoReply, setShowAutoReply] = useState(false);

  // Archive Settings State
  const [archiveSettings, setArchiveSettings] = useState<ArchiveSettings | null>(null);
  const [showArchiveSettings, setShowArchiveSettings] = useState(false);

  // New Conversation State
  const [showNewConv, setShowNewConv] = useState(false);
  const [clientList, setClientList] = useState<{ id: string; name: string }[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const activeConvIdRef = useRef<string | null>(null);
  const loadConversationsRef = useRef<() => Promise<void>>(async () => {});
  const loadMessagesRef = useRef<(convId: string) => Promise<void>>(async () => {});
  const isMountedRef = useRef(true);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Data Loading ──────────────────────────────────────────

  const loadConversations = useCallback(async () => {
    try {
      const params: any = {};
      if (search) params.search = search;
      const res = await chatApi.conversations(params);
      setConversations(res.data.items || []);
    } catch {
      // silently handled
    }
  }, [search]);

  const loadArchived = useCallback(async () => {
    try {
      const res = await chatApi.archived();
      setArchivedConvs(res.data.items || []);
    } catch {
      // silently handled
    }
  }, []);

  const loadMessages = useCallback(async (convId: string) => {
    try {
      const res = await chatApi.messages(convId, { limit: 100 });
      setMessages(res.data.items || []);
      // Mark as read
      chatApi.markRead(convId).catch(() => {});
    } catch {
      // silently handled
    }
  }, []);

  const loadAutoReplies = useCallback(async () => {
    try {
      const res = await chatApi.autoReplies();
      setAutoReplies(res.data || []);
    } catch {
      // silently handled
    }
  }, []);

  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);
  useEffect(() => { loadConversationsRef.current = loadConversations; }, [loadConversations]);
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadConversations(), loadArchived(), loadAutoReplies()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-search
  useEffect(() => {
    const t = setTimeout(() => loadConversations(), 300);
    return () => clearTimeout(t);
  }, [search, loadConversations]);

  // WebSocket for real-time updates (with polling fallback)
  const wsRef = useRef<WebSocket | null>(null);
  const wsConnected = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return; // No WS in development without API URL
    const baseUrl = apiUrl.replace(/^http/, 'ws');
    const wsUrl = `${baseUrl}/ws/chat?token=${token}`;

    let reconnectTimer: ReturnType<typeof setTimeout>;

    const connect = () => {
      if (!isMountedRef.current) return;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          wsConnected.current = true;
          reconnectAttempts.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_message') {
              loadConversationsRef.current();
              if (activeConvIdRef.current === data.conversation_id) {
                loadMessagesRef.current(data.conversation_id);
              }
            } else if (data.type === 'conversations_updated') {
              loadConversationsRef.current();
            }
          } catch { /* ignore non-JSON */ }
        };

        ws.onclose = () => {
          wsConnected.current = false;
          wsRef.current = null;
          if (isMountedRef.current) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 60000);
            reconnectAttempts.current++;
            reconnectTimer = setTimeout(connect, delay);
          }
        };

        ws.onerror = () => {
          ws.close();
        };

        // Keep alive ping every 30s
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);

        ws.addEventListener('close', () => {
          if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        });
      } catch {
        if (isMountedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 60000);
          reconnectAttempts.current++;
          reconnectTimer = setTimeout(connect, delay);
        }
      }
    };

    connect();

    return () => {
      isMountedRef.current = false;
      clearTimeout(reconnectTimer);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      wsRef.current?.close();
      wsConnected.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling fallback: only if WebSocket is not connected
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsConnected.current) return; // Skip if WS active
      loadConversations();
      if (activeConvId) loadMessages(activeConvId);
    }, 10000);
    return () => clearInterval(interval);
  }, [activeConvId, loadConversations, loadMessages]);

  // Load messages when conversation changes
  useEffect(() => {
    if (activeConvId) {
      loadMessages(activeConvId);
    }
  }, [activeConvId, loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── New Conversation ──────────────────────────────────────

  const openNewConv = async () => {
    setShowNewConv(true);
    setClientSearch('');
    setLoadingClients(true);
    try {
      const res = await clientsApi.list({ limit: 200 });
      const clients = (res.data.items || res.data || []).map((c: any) => ({
        id: c.id,
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unbekannt',
      }));
      setClientList(clients);
    } catch {
      showToast('Klienten konnten nicht geladen werden', 'error');
    }
    setLoadingClients(false);
  };

  const startConversation = async (clientId: string) => {
    try {
      const res = await chatApi.startConversation(clientId);
      const conv = res.data;
      setShowNewConv(false);
      await loadConversations();
      setActiveConvId(conv.id);
      setTab('aktiv');
    } catch {
      showToast('Konversation konnte nicht gestartet werden', 'error');
    }
  };

  const filteredClients = clientSearch
    ? clientList.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
    : clientList;

  // Exclude clients that already have an active conversation
  const existingClientIds = new Set(conversations.map(c => c.client_id));
  const availableClients = filteredClients.filter(c => !existingClientIds.has(c.id));

  // ─── Actions ───────────────────────────────────────────────

  const handleSend = async () => {
    if (!activeConvId || !newMessage.trim() || sending) return;
    setSending(true);
    try {
      await chatApi.sendMessage(activeConvId, { content: newMessage.trim() });
      setNewMessage('');
      await loadMessages(activeConvId);
      await loadConversations();
    } catch (err) {
      showToast('Nachricht konnte nicht gesendet werden', 'error');
    }
    setSending(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleArchive = async (convId: string) => {
    try {
      await chatApi.archive(convId, { reason: 'manual' });
      showToast('Konversation archiviert', 'success');
      if (activeConvId === convId) setActiveConvId(null);
      await Promise.all([loadConversations(), loadArchived()]);
    } catch (err) {
      showToast('Fehler beim Archivieren', 'error');
    }
  };

  const handleUnarchive = async (convId: string) => {
    try {
      await chatApi.unarchive(convId);
      showToast('Konversation wiederhergestellt', 'success');
      await Promise.all([loadConversations(), loadArchived()]);
    } catch (err) {
      showToast('Fehler beim Wiederherstellen', 'error');
    }
  };

  const handleToggleAutoReply = async (ruleType: string) => {
    try {
      await chatApi.toggleAutoReply(ruleType);
      await loadAutoReplies();
    } catch (err) {
      showToast('Fehler beim Umschalten', 'error');
    }
  };

  // ─── Active Conversation Data ──────────────────────────────

  const activeConv = conversations.find(c => c.id === activeConvId)
    || archivedConvs.find(c => c.id === activeConvId);

  const busyRule = autoReplies.find(r => r.rule_type === 'busy');
  const isBusy = busyRule?.is_active || false;

  const displayConvs = tab === 'aktiv' ? conversations : archivedConvs;

  // ─── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="nachrichten-container">
        <div className="nachrichten-loading">
          <MessageSquare size={48} strokeWidth={1.5} />
          <p>Nachrichten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nachrichten-container">
      {/* ── Sidebar ── */}
      <div className={`nachrichten-sidebar ${activeConvId ? 'hidden-mobile' : ''}`}>
        {/* Header */}
        <div className="nachrichten-sidebar-header">
          <div className="sidebar-header-top">
            <h2><MessageSquare size={20} /> Nachrichten</h2>
            <button
              className="new-conv-btn"
              data-tour="msg-new-btn"
              onClick={openNewConv}
              title="Neue Konversation"
            >
              <Plus size={16} />
            </button>
          </div>
          <button
            className={`busy-toggle ${isBusy ? 'active' : ''}`}
            data-tour="msg-toggle"
            onClick={() => handleToggleAutoReply('busy')}
            title={isBusy ? 'Im Coaching — Auto-Reply aktiv' : 'Verfügbar — keine Auto-Reply'}
          >
            <User size={14} />
            {isBusy ? 'Im Coaching' : 'Verfügbar'}
          </button>
        </div>

        {/* Search */}
        <div className="nachrichten-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Konversation suchen..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Tabs */}
        <div className="nachrichten-tabs" data-tour="msg-tabs">
          <button
            className={tab === 'aktiv' ? 'active' : ''}
            onClick={() => setTab('aktiv')}
          >
            Aktiv ({conversations.length})
          </button>
          <button
            className={tab === 'archiv' ? 'active' : ''}
            onClick={() => { setTab('archiv'); loadArchived(); }}
          >
            Archiv ({archivedConvs.length})
          </button>
        </div>

        {/* Conversation List */}
        <div className="conversation-list" data-tour="msg-inbox">
          {displayConvs.length === 0 && (
            <div className="empty-state">
              {tab === 'aktiv' ? 'Keine aktiven Konversationen' : 'Kein Archiv vorhanden'}
            </div>
          )}
          {displayConvs.map(conv => (
            <div
              key={conv.id}
              className={`conversation-item ${activeConvId === conv.id ? 'active' : ''} ${conv.unread_count > 0 ? 'unread' : ''}`}
              onClick={() => setActiveConvId(conv.id)}
            >
              <div className="conv-avatar" style={{ background: getAvatarColor(conv.client_name) }}>
                {getInitials(conv.client_name)}
                <span className={`channel-badge ${conv.preferred_channel}`}>
                  {channelIcon(conv.preferred_channel)}
                </span>
              </div>
              <div className="conv-info">
                <div className="conv-name">{conv.client_name || 'Unbekannt'}</div>
                <div className="conv-preview">{conv.last_message_preview || 'Keine Nachrichten'}</div>
              </div>
              <div className="conv-meta">
                <div className="conv-time">{formatTime(conv.last_message_at)}</div>
                {conv.unread_count > 0 && (
                  <div className="conv-unread">{conv.unread_count}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Settings Buttons */}
        <div className="nachrichten-sidebar-footer">
          <button onClick={() => setShowAutoReply(!showAutoReply)}>
            <Clock size={14} /> Auto-Replies
          </button>
          <button onClick={() => {
            setShowArchiveSettings(!showArchiveSettings);
            if (!archiveSettings) {
              chatApi.archiveSettings().then(r => setArchiveSettings(r.data)).catch(() => {});
            }
          }}>
            <Settings size={14} /> Archiv
          </button>
        </div>
      </div>

      {/* ── Chat Area ── */}
      <div className={`nachrichten-chat ${!activeConvId ? 'hidden-mobile' : ''}`}>
        {!activeConvId ? (
          <div className="chat-empty">
            <MessageSquare size={64} strokeWidth={1} />
            <h3>Wählen Sie eine Konversation</h3>
            <p>Klicken Sie links auf einen Klienten, um den Chat zu öffnen.</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <div className="chat-header-left">
                <button
                  className="back-btn mobile-only"
                  onClick={() => setActiveConvId(null)}
                >
                  ←
                </button>
                <h3>{activeConv?.client_name || 'Unbekannt'}</h3>
                <span className={`channel-tag ${activeConv?.preferred_channel || 'in_app'}`}>
                  {channelIcon(activeConv?.preferred_channel || 'in_app')}
                  {channelLabel(activeConv?.preferred_channel || 'in_app')}
                </span>
              </div>
              <div className="chat-header-right">
                {activeConv?.is_archived ? (
                  <button
                    className="icon-btn"
                    title="Wiederherstellen"
                    onClick={() => handleUnarchive(activeConv.id)}
                  >
                    <ArchiveRestore size={18} />
                  </button>
                ) : (
                  <button
                    className="icon-btn"
                    title="Archivieren"
                    onClick={() => activeConv && handleArchive(activeConv.id)}
                  >
                    <Archive size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="chat-messages">
              {messages.length === 0 && (
                <div className="chat-system-msg">
                  Noch keine Nachrichten. Schreiben Sie die erste Nachricht!
                </div>
              )}
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`chat-msg ${msg.sender_type}`}
                >
                  {msg.sender_type === 'system' ? (
                    <div className="system-content">
                      <Clock size={12} /> {msg.content}
                    </div>
                  ) : (
                    <>
                      <div className="msg-content">{msg.content}</div>
                      <div className="msg-meta">
                        {formatTime(msg.created_at)}
                        {msg.sender_type === 'coach' && msg.status === 'sent' && ' ✓'}
                        {msg.sender_type === 'coach' && msg.status === 'delivered' && ' ✓✓'}
                        {msg.sender_type === 'coach' && msg.status === 'read' && ' ✓✓'}
                      </div>
                    </>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {!activeConv?.is_archived && (
              <div className="chat-input-bar">
                <textarea
                  className="chat-input"
                  placeholder="Nachricht eingeben..."
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  rows={1}
                />
                <button
                  className="send-btn"
                  onClick={handleSend}
                  disabled={!newMessage.trim() || sending}
                >
                  <Send size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Auto-Reply Sidebar ── */}
      {showAutoReply && (
        <div className="settings-panel">
          <div className="settings-panel-header">
            <h3>Auto-Replies</h3>
            <button onClick={() => setShowAutoReply(false)}>×</button>
          </div>
          <div className="settings-panel-body">
            {autoReplies.map(rule => (
              <div key={rule.id} className="autoreply-card">
                <div className="autoreply-header">
                  <div className="autoreply-left">
                    {ruleTypeIcon(rule.rule_type)}
                    <span>{ruleTypeLabel(rule.rule_type)}</span>
                  </div>
                  <button
                    className={`toggle-btn ${rule.is_active ? 'active' : ''}`}
                    onClick={() => handleToggleAutoReply(rule.rule_type)}
                  >
                    {rule.is_active ? 'Ein' : 'Aus'}
                  </button>
                </div>
                <div className="autoreply-preview">{rule.message_de}</div>
                {rule.schedule && rule.rule_type === 'outside_hours' && (
                  <div className="autoreply-schedule">
                    {rule.schedule.days?.join(', ')} {rule.schedule.start_time}-{rule.schedule.end_time}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── New Conversation Modal ── */}
      {showNewConv && (
        <div className="new-conv-overlay" onClick={() => setShowNewConv(false)}>
          <div className="new-conv-modal" onClick={e => e.stopPropagation()}>
            <div className="new-conv-header">
              <h3>Neue Konversation</h3>
              <button onClick={() => setShowNewConv(false)}><X size={18} /></button>
            </div>
            <div className="new-conv-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Klient suchen..."
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="new-conv-list">
              {loadingClients && <div className="new-conv-loading">Wird geladen...</div>}
              {!loadingClients && availableClients.length === 0 && (
                <div className="new-conv-empty">
                  {clientList.length === 0
                    ? 'Keine Klienten vorhanden'
                    : clientSearch
                      ? 'Kein Klient gefunden'
                      : 'Alle Klienten haben bereits eine Konversation'}
                </div>
              )}
              {availableClients.map(client => (
                <div
                  key={client.id}
                  className="new-conv-client"
                  onClick={() => startConversation(client.id)}
                >
                  <div className="conv-avatar small" style={{ background: getAvatarColor(client.name) }}>
                    {getInitials(client.name)}
                  </div>
                  <span>{client.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Archive Settings Sidebar ── */}
      {showArchiveSettings && archiveSettings && (
        <div className="settings-panel">
          <div className="settings-panel-header">
            <h3>Archiv-Einstellungen</h3>
            <button onClick={() => setShowArchiveSettings(false)}>×</button>
          </div>
          <div className="settings-panel-body">
            <div className="setting-row">
              <label>Archivieren bei Fall-Abschluss</label>
              <button
                className={`toggle-btn ${archiveSettings.auto_archive_on_case_close ? 'active' : ''}`}
                onClick={() => {
                  const updated = { ...archiveSettings, auto_archive_on_case_close: !archiveSettings.auto_archive_on_case_close };
                  setArchiveSettings(updated);
                  chatApi.updateArchiveSettings(updated).catch(() => {});
                }}
              >
                {archiveSettings.auto_archive_on_case_close ? 'Ein' : 'Aus'}
              </button>
            </div>
            <div className="setting-row">
              <label>Inaktivität (Tage)</label>
              <input
                type="number"
                value={archiveSettings.auto_archive_inactive_days}
                min={30}
                max={365}
                onChange={e => {
                  const updated = { ...archiveSettings, auto_archive_inactive_days: parseInt(e.target.value) || 180 };
                  setArchiveSettings(updated);
                }}
                onBlur={() => chatApi.updateArchiveSettings(archiveSettings).catch(() => {})}
              />
            </div>
            <div className="setting-row">
              <label>Aufbewahrung (Monate)</label>
              <input
                type="number"
                value={archiveSettings.retention_months}
                min={6}
                max={60}
                onChange={e => {
                  const updated = { ...archiveSettings, retention_months: parseInt(e.target.value) || 24 };
                  setArchiveSettings(updated);
                }}
                onBlur={() => chatApi.updateArchiveSettings(archiveSettings).catch(() => {})}
              />
            </div>
            <div className="setting-row">
              <label>Archiv-PDFs in Google Drive</label>
              <button
                className={`toggle-btn ${archiveSettings.auto_upload_drive ? 'active' : ''}`}
                onClick={() => {
                  const updated = { ...archiveSettings, auto_upload_drive: !archiveSettings.auto_upload_drive };
                  setArchiveSettings(updated);
                  chatApi.updateArchiveSettings(updated).catch(() => {});
                }}
              >
                {archiveSettings.auto_upload_drive ? 'Ein' : 'Aus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
